import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { generateDepositLink, createAndPayAtDesk, type DeskCreateAndPayResult } from '@/domain/depositFlow';
import { intentFailureCopy } from '@/api/intents';
import { useUIStore } from '@/state/uiStore';
import { recallOverlappingHolds } from '@/domain/resendRoutingFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { depositForDuration, sessionLengthForDuration } from '@/domain/pricing';
import { eventBus } from '@/events/eventBus';
import type { Booking } from '@/domain/types';
import { startNoEarlierThanNow } from '@/utils/slotClip';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { customerDepositUrl } from '@/utils/customerLinks';
import { customerName } from '@/utils/format';

/**
 * How the deposit is taken when a Sweeper gap is filled (board 08, the client's
 * choice on the call):
 *  - 'phone-eftpos': card over the phone now — the slot locks instantly.
 *  - 'dated-link': a dated + artist deposit link with a firm 24h hold.
 */
export type GapFillDepositMethod = 'phone-eftpos' | 'dated-link';

export interface GapFillInput {
  customerId: string;
  artistId: string;
  startIso: string;
  endIso: string;
  method: GapFillDepositMethod;
  /** Free-text piece/brief captured on the call, if any. */
  designBrief?: string;
  /** Channel the dated link is sent on (dated-link only). Defaults to SMS. */
  channel?: 'sms' | 'meta-dm' | 'ig-dm' | 'email';
  /** 'phone-eftpos' only (D05-11): the client's email, needed to take the money. */
  contactEmail?: string;
}

export interface GapFillResult {
  bookingId?: string;
  depositId?: string;
  /**
   * 'phone-eftpos' only: settles when the server answers the create-and-pay. The
   * caller keeps its modal open until `ok` (a failure has already been toasted).
   */
  settled?: Promise<DeskCreateAndPayResult>;
}

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

function slotMinutes(startIso: string, endIso: string): number {
  return Math.max(15, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
}

/**
 * Fill a Sweeper gap, confirmed on the call (bd tv-pt8, board 08 + spine 01).
 *
 * A Sweeper gap-fill is ALWAYS verbally confirmed on the spot and bypasses the
 * pre-appointment confirmations cadence: the booking carries `confirmedOnCall`,
 * so `confirmationWorklist` never surfaces it as a call/text lane. The
 * confirmation message + waiver still go out via the shared confirm hook the
 * moment the booking is confirmed. Confirm-on-call is a fixed property of the
 * gap-fill, not an operator toggle.
 *
 * The operator's one choice is how the deposit is taken:
 *  - 'phone-eftpos': card over the phone now. The slot locks once the server
 *    commits the create-and-pay — a paid EFTPOS deposit against a confirmed
 *    booking; the waiver + calendar invite go out only then.
 *  - 'dated-link': a dated + artist deposit link with a firm 24h hold. The slot
 *    is held; paid within 24h it confirms, otherwise it lapses onto the Sweeper's
 *    Missed-deposits list (no 2h overbook).
 *
 * "Now" is the sim clock. No-op (empty result) when the customer is unknown.
 */
export function confirmGapFillOnCall(raw: GapFillInput): GapFillResult {
  const brain = useBrainStore.getState();
  const customer = brain.customers[raw.customerId];
  if (!customer) return {};

  const at = nowIso();
  // Never booked into hours already past (tv-2na.10.3): a stale gap start moves
  // to the next quarter hour after the sim clock; the gap's end is kept (a
  // wholly-past gap keeps its length from the new start instead).
  const startIso = startNoEarlierThanNow(raw.startIso, at);
  const endIso =
    Date.parse(raw.endIso) > Date.parse(startIso)
      ? raw.endIso
      : new Date(Date.parse(startIso) + (Date.parse(raw.endIso) - Date.parse(raw.startIso))).toISOString();
  const input: GapFillInput = { ...raw, startIso, endIso };
  const durationMin = slotMinutes(input.startIso, input.endIso);
  const sessionLength = sessionLengthForDuration(durationMin);
  const amountAud = depositForDuration(durationMin);
  // The ARTIST's studio (B7, 75nh): a cross-studio fill (all artists equal) must
  // read as booked on the artist's calendar, which is read by studio.
  const studioId = brain.artists[input.artistId]?.studioId ?? customer.primaryStudioId;

  if (input.method === 'dated-link') {
    // Firm 24h hold: generateDepositLink mints the held ghost booking + a pending
    // deposit (24h expiry) against the exact slot, flagged confirmed-on-call in the
    // same write so it skips the cadence even before the deposit lands; the confirm message +
    // waiver fire on payment (the existing paid-webhook confirm path).
    // L1 (14ym9bva056): the hold-slot intent also texts the client the pay link
    // (server-authored, once). Store fallback has no server: the line is written here.
    const deposit = generateDepositLink({
      customerId: input.customerId,
      studioId,
      artistId: input.artistId,
      amountAud,
      channel: input.channel ?? 'sms',
      sessionLength,
      source: 'agent-initiated-chat',
      // W5 (DECIDED 24 Sep): a Sweeper-sent link — unpaid past 24h it lands on the
      // Sweeper's missed-deposits list (not the Agent's / Ops's).
      depositSource: 'gap-fill',
      sentByRole: 'sweeper',
      discussedSlot: { artistId: input.artistId, startIso: input.startIso, endIso: input.endIso },
      // Sweeper gap-fill links are a firm 24h lock with no 2h overbook, even inside
      // this week (tv-nid) - unlike agent dated-links, which are overbookable.
      firmHold: true,
      // Confirmed on the call: carried inside the hold itself (7kv1).
      confirmedOnCall: true,
      textLink: true,
      ...(input.designBrief ? { pieceName: input.designBrief } : {}),
    });
    if (isStoreFallbackEnabled()) {
      brain.addConversationEntry(input.customerId, {
        id: `convo-dep-${deposit.id}`,
        customerId: input.customerId,
        channel: deposit.channelSent,
        direction: 'out',
        body: `Sent ${customerName(customer)} a $${amountAud} deposit link, held for 24 hours: ${customerDepositUrl(deposit.id)}`,
        timestamp: at,
        authorRole: 'agent',
        authorName: 'You',
        depositId: deposit.id,
      });
    }
    eventBus.publish('gapfill:confirmed-on-call', {
      bookingId: deposit.heldBookingId,
      depositId: deposit.id,
      method: input.method,
      at,
    });
    return { bookingId: deposit.heldBookingId, depositId: deposit.id };
  }

  // 'phone-eftpos': lock instantly. The confirmed booking + its paid EFTPOS
  // deposit land through ONE create-and-pay intent (tv-2na.8.3) — the old
  // upsertBooking + recordManualPayment pair deadlocked on the booking <-> deposit
  // FK and was dropped, AFTER the waiver/SMS had already gone out. Every
  // side-effect (sequence clear, waiver + calendar invite, overbook recall, the
  // event) now runs only once the server says ok. Ids are deterministic per
  // customer + slot, so re-confirming the same gap after a transient failure is
  // an idempotent retry.
  const slotMs = new Date(input.startIso).getTime();
  const bookingId = `bk-gapfill-${input.customerId}-${slotMs}`;
  const depositId = `dep-gapfill-${input.customerId}-${slotMs}`;
  const booking: Booking = {
    id: bookingId,
    customerId: input.customerId,
    studioId,
    artistId: input.artistId,
    start: input.startIso,
    durationMin,
    sessionLength,
    status: 'confirmed',
    // Confirmed on the call: skips the confirmations cadence (confirmedAt is the
    // server's to leave empty — tv-xue).
    confirmedOnCall: true,
    designBrief: input.designBrief ?? 'Gap filled on the call.',
    referencePhotos: [],
    source: 'agent-initiated-chat',
    createdAt: at,
  };
  const settled = createAndPayAtDesk({
    booking,
    depositId,
    amountAud,
    method: 'eftpos',
    channel: 'card',
    ...(input.contactEmail ? { contactEmail: input.contactEmail } : {}),
    onConfirmed: () => {
      const b = useBrainStore.getState();
      b.clearSequenceState(input.customerId);
      onBookingConfirmed(bookingId, { paidOnServer: true });
      // Slot locked on the call: void + re-call any other overbookable hold on it.
      recallOverlappingHolds(bookingId);
      eventBus.publish('gapfill:confirmed-on-call', { bookingId, depositId, method: input.method, at });
    },
    onFailed: (f) => {
      const copy = intentFailureCopy(f);
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: copy.title,
        body: copy.body,
        ttlMs: 10000,
        customerId: input.customerId,
      });
    },
  });
  return { bookingId, depositId, settled };
}
